<template>
  <section-with-divider label="配乗表">
    <template v-slot:header>
      <div class="header-title">配乗一覧表</div>
    </template>
    <div class="grid-wrapper">
      <div class="grid-content">
        <div class="header">乗船中</div>
        <ag-grid-vue
          ref="agGridOnboardCrewAssignmentManagement"
          :gridOptions="onboardGridOptions"
          class="ag-theme-material w-full ag-grid-table"
          :columnDefs="onboardColumnDefs"
          :defaultColDef="defaultColDef"
          :rowData="filteredOnboardRows"
          rowSelection="multiple"
          colResizeDefault="shift"
          :animateRows="true"
          :floatingFilter="false"
          :suppressPaginationPanel="true"
          :suppressDragLeaveHidesColumns="true"
          :suppressRowClickSelection="true"
          :suppressCellFocus="true"
          :suppressNoRowsOverlay="false"
          @grid-ready="onGridReady"
        />
      </div>
      <div class="disembark-crew-content">
        <div class="header">下船中船員</div>
        <div class="disembark-crew-scroll">
          <div
            v-for="crewRole in this.getSortedCrewRoles"
            :key="crewRole.id"
            class="disembark-crew-list-box"
          >
            <div class="role-name">{{ crewRole.name }}</div>
            <ashore-crew-card
              :crew-list="filteredRoleDisembarkData(crewRole.id)"
            />
          </div>
        </div>
      </div>
    </div>
  </section-with-divider>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import { createNamespacedHelpers } from "vuex";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import moment from "moment";
import { HandleHttpErrorService } from "../utils/errors";
import SideDrawer from "../components/SideDrawer.vue";
import AshoreCrewCard from "../components/AshoreCrewCard.vue";
import SectionWithDivider from "../components/SectionWithDivider.vue";
import HeaderCellRendererVessel from "./cell-renderer/HeaderCellRendererVessel.vue";
import CellRendererCrewCard from "./cell-renderer/CellRendererCrewCard.vue";

const { mapActions, mapGetters } = createNamespacedHelpers(
  "crewAssignmentManagement"
);

export default {
  name: "CrewAssignmentManagementGrid",
  mixins: [DeviceInfoMixin, ColorMixin],
  components: {
    AgGridVue,
    SectionWithDivider,
    SideDrawer,
    AshoreCrewCard,
    HeaderCellRendererVessel,
    CellRendererCrewCard,
    AshoreCrewCard,
  },
  data() {
    return {
      selectedDate: moment().format("YYYY-MM-DD"),

      manningVesselsData: [],
      onboardCrewsData: [],
      ashoreCrewsData: [],
      crewRolesData: [],
      manningVesselsAlertsData: [],
      crewAlertsData: [],
      crewAlertSettingsData: [],

      columnApi: null,
      gridApi: null,
      gridColumnApi: null,

      onboardGridOptions: {
        localeText: {
          noRowsToShow:
            '<div id="no-rows-crew-list" class="aisea-text-16-400-black">表示する乗船中の配乗一覧情報がありません</div>',
        },
        context: {
          componentParent: this,
        },
      },
      defaultColDef: {
        resizable: true,
        sortable: false,
        unSortIcon: true,
        comparator: () => {
          return;
        },
      },

      nowDate: new Date(),
    };
  },
  computed: {
    ...mapGetters([
      "getManningVessels",
      "getOnboardingCrews",
      "getAshoreCrews",
      "getCrewsRoles",
      "getManningVesselsAlerts",
      "getCrewAlerts",
      "getCrewAlertSettings",
    ]),
    onboardColumnDefs() {
      const columns = [
        {
          headerName: "役職 / 船名",
          field: "role_vessel_name",
          width: 90,
          autoHeight: true,
          pinned: "left",
          headerClass: "vessel-role-header-cell",
          cellClass: "vessel-role-cell",
        },
        ...this.manningVesselsData.map(manningVessel => {
          const vesselAlert =
            this.manningVesselsAlertsData.length > 0
              ? this.manningVesselsAlertsData[0].vessels.find(
                  vessel => vessel.vessel_info.id === manningVessel.id
                )
              : undefined;
          return {
            // --- ヘッダーの設定 ---
            headerName: manningVessel.name,
            headerClass: "ag-header-cell-text",
            headerComponent: "HeaderCellRendererVessel",
            headerComponentParams: {
              name: `${manningVessel.name}`,
              alertInfo: vesselAlert ? vesselAlert.alert_info : [],
            },

            // --- セルの設定 ---
            field: `vessel_${manningVessel.id}`,
            width: 160,
            autoHeight: true,
            cellRenderer: "CellRendererCrewCard",
            cellRendererParams: {
              vesselName: manningVessel.name,
            },
          };
        }),
      ];
      return columns;
    },
    getSortedCrewRoles() {
      return [...this.crewRolesData].sort(
        (a, b) => a.sort_order - b.sort_order
      );
    },
    filteredOnboardRows() {
      const sortedRoles = this.getSortedCrewRoles;

      /** * マップの作成: map[vesselId][roleId] = [crew1, crew2, ...]
       * 配列で保持することで複数人に対応
       */
      const onboardCrewMap = this.onboardCrewsData.reduce(
        (map, onboardCrew) => {
          const currentManningVesselCrew =
            onboardCrew.current_manning_vessel_crew;
          const vesselId =
            currentManningVesselCrew.enterprise_manning_vessel_id;

          if (!map[vesselId]) map[vesselId] = {};

          currentManningVesselCrew.enterprise_manning_vessel_crew_roles.forEach(
            role => {
              const roleId = role.enterprise_crew_role_id;

              // 配列を初期化
              if (!map[vesselId][roleId]) {
                map[vesselId][roleId] = [];
              }

              // データを配列に追加
              map[vesselId][roleId] = [
                ...map[vesselId][roleId],
                {
                  id: onboardCrew.crew.id,
                  name: onboardCrew.crew.name,
                  startAt: role.start_at,
                  endAt: role.end_at,
                  crewErrors: this.getCrewErrorsById(onboardCrew.crew.id),
                },
              ];
            }
          );

          return map;
        },
        {}
      );

      return sortedRoles.map(role => {
        return {
          role_vessel_name: role.name,
          ...Object.fromEntries(
            this.manningVesselsData.map(vessel => {
              const onboardCrewVessel = onboardCrewMap[vessel.id];
              // 該当がない場合は空配列を返すようにする
              const crewsInRole =
                onboardCrewVessel && onboardCrewVessel[role.id]
                  ? onboardCrewVessel[role.id]
                  : [];

              return [`vessel_${vessel.id}`, crewsInRole];
            })
          ),
        };
      });
    },
  },
  methods: {
    ...mapActions([
      "fetchEnterpriseManningVessels",
      "fetchEnterpriseOnboardingCrews",
      "fetchEnterpriseAshoreCrews",
      "fetchEnterpriseCrewsRoles",
      "fetchEnterpriseManningVesselAlerts",
      "fetchEnterpriseCrewsAlerts",
      "fetchEnterpriseCrewAlertSettings",
    ]),
    async onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      const fetchEnterpriseManningVesselsParams = {
        details: ["MANNING_VESSEL_ROLE", "MANNING_VESSEL_ROLE_POSITION"],
      };
      const fetchEnterpriseOnboardingCrewsParams = {
        manning_vessel_details: ["MANNING_VESSEL_CREW_ROLE"],
      };
      const fetchEnterpriseAshoreCrewsParams = {
        details: ["CREW_AVAILABLE_ROLE"],
      };
      const fetchEnterpriseManningVesselAlertsParams = {
        kinds: ["REPLACEMENT_CREW_NOT_ASSIGNED"],
      };

      const fetchEnterpriseCrewsAlertsParams = {
        kinds: [
          "EMBARKATION_STANDBY",
          "DISEMBARKATION_STANDBY",
          "DAY_OFF_REQUEST_DUPLICATED",
        ],
      };

      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        // データの取得
        await Promise.all([
          this.fetchEnterpriseManningVessels(
            fetchEnterpriseManningVesselsParams
          ),
          this.fetchEnterpriseOnboardingCrews(
            fetchEnterpriseOnboardingCrewsParams
          ),
          this.fetchEnterpriseAshoreCrews(fetchEnterpriseAshoreCrewsParams),
          this.fetchEnterpriseCrewsRoles(),
          this.fetchEnterpriseManningVesselAlerts(
            fetchEnterpriseManningVesselAlertsParams
          ),
          this.fetchEnterpriseCrewsAlerts(fetchEnterpriseCrewsAlertsParams),
          this.fetchEnterpriseCrewAlertSettings(),
        ]);

        // ローカルデータに格納
        this.manningVesselsData = this.getManningVessels;
        this.onboardCrewsData = this.getOnboardingCrews;
        this.ashoreCrewsData = this.getAshoreCrews;
        this.crewRolesData = this.getCrewsRoles;
        this.manningVesselsAlertsData = this.getManningVesselsAlerts;
        this.crewAlertsData = this.getCrewAlerts;
        this.crewAlertSettingsData = this.getCrewAlertSettings;
      } catch (error) {
        const message = HandleHttpErrorService.handleErrorNotificationMessage(
          error
        );
        this.$vs.notify({
          title: "通信に失敗しました",
          text: message,
          color: "danger",
        });
        return;
      } finally {
        this.$vs.loading.close();
      }
    },
    getCrewErrorsById(crewId) {
      if (!crewId || !Array.isArray(this.crewAlertsData)) {
        return [];
      }

      return this.crewAlertsData.flatMap(alert =>
        alert.crews
          .filter(c => c.crew_info.id === crewId)
          .map(c => ({
            ...c.alert_info[0],
            alert_kind: alert.alert_kind,
          }))
      );
    },
    filteredRoleDisembarkData(roleId) {
      return this.ashoreCrewsData
        .filter(ashoreCrew => {
          return ashoreCrew.crew.crew_available_roles.some(availableRole => {
            return (
              availableRole.enterprise_crew_role_id === roleId &&
              availableRole.is_appointed
            );
          });
        })
        .map(ashoreCrew => {
          return {
            id: ashoreCrew.crew.id,
            name: ashoreCrew.crew.name,
            start_at: ashoreCrew.start_at,
            days_off_while_ashore: ashoreCrew.days_off_while_ashore,
            crewErrors: this.getCrewErrorsById(ashoreCrew.crew.id),
          };
        });
    },
  },
  mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    if (routerViewElement[0]) {
      routerViewElement[0].style.padding = "2.2rem";
    }
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.header-title {
  padding: 18px 0px;
  font-size: 18px;
  font-weight: 600;
  font-family: $aisea-font-family;
  line-height: 26px;
  color: $aisea-dark-gray;
}

.grid-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 30px 30px 30px;
  overflow: hidden;

  .grid-content {
    height: 70vh;
    flex: 8 1 560px;
    display: flex;
    flex-direction: column;

    border-radius: 6px;
    border: 1px solid $aisea-light-white;
  }

  .header {
    display: flex;
    align-items: center;
    border-bottom: 1px solid $aisea-light-white;
    padding: 8px;

    font-size: 16px;
    font-weight: 600;
    font-family: $aisea-font-family;
    line-height: 26px;
    color: $aisea-dark-gray;
  }

  .disembark-crew-content {
    height: 70vh;
    flex: 2 1 120px;
    display: flex;
    flex-direction: column;

    border-radius: 6px;
    border: 1px solid $aisea-light-white;

    .disembark-crew-scroll {
      flex: 1;
      overflow: auto;
    }

    .disembark-crew-list-box {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 8px;
      border-bottom: 1px solid $aisea-light-white;

      .role-name {
        font-family: $aisea-font-family;
        font-weight: 600;
        font-size: 10px;
        line-height: 16px;
        vertical-align: middle;
      }
    }
  }

  ::v-deep .ag-grid-table {
    height: 100%;

    .ag-header-cell {
      padding: 0 !important;
      box-sizing: border-box;
      border-right: 1px solid $aisea-light-white !important;
    }

    .ag-header-cell-label {
      padding: 12px 8px !important;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }

    .ag-header-cell-text {
      margin: 0 !important;
      font-size: 10px;
      font-family: $aisea-font-family;
      font-weight: 600;
      color: $aisea-dark-gray;
      text-align: left;
      white-space: pre-line;
    }

    .ag-cell {
      box-sizing: border-box;
      border-right: 1px solid $aisea-light-white !important;
      padding: 0px !important;
    }

    .ag-cell-value {
      width: 100%;
      display: block;
      margin: 0;
      padding: 0;
      line-height: normal;
    }

    .vessel-role-cell {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      box-sizing: border-box;
      border-right: 1px solid $aisea-light-white !important;
      padding: 12px 8px !important;
      font-size: 10px;
      font-family: $aisea-font-family;
      font-weight: 600;
      color: $aisea-dark-gray;
      text-align: left;
      white-space: pre-line;
    }
  }
}
</style>
